import { PerspectiveCamera } from "@react-three/drei";
import { useFrame, useThree } from "@react-three/fiber";
import gsap from "gsap/gsap-core";
import React, { useEffect, useRef } from "react";
import useDeviceOrientation from "../useDeviceOrientation";
import { useMousePosition } from "../useMousePosition";
import useStore from "../useStore";

export const camerasPositions = [
  {
    zoomOutPosition: {
      x: -9,
      y: 4,
      z: 20,
    },
    zoomOutRotation: {
      x: 0,
      y: -0.6,
      z: 0,
    },
    position: {
      x: -1,
      y: 6,
      z: 9,
    },
    rotation: {
      x: -0.2,
      y: -0.1,
      z: 0,
    },
  },
  {
    zoomOutPosition: {
      x: -4,
      y: 6,
      z: 13,
    },
    zoomOutRotation: {
      x: -0.2,
      y: -0.1,
      z: 0,
    },
    position: {
      x: 0,
      y: 5,
      z: 10,
    },
    rotation: {
      x: -0.2,
      y: -0.6,
      z: 0,
    },
  },
  {
    zoomOutPosition: {
      x: 14,
      y: 4,
      z: 12,
    },
    zoomOutRotation: {
      x: 0.2,
      y: 0.85,
      z: 0,
    },
    position: {
      x: 6,
      y: 7,
      z: 9,
    },
    rotation: {
      x: -0.4,
      y: 0.85,
      z: 0,
    },
  },
  {
    zoomOutPosition: {
      x: -2.2,
      y: 10.5,
      z: 4,
    },
    zoomOutRotation: {
      x: -1.3,
      y: 0.85,
      z: 0,
    },
    position: {
      x: -2.2,
      y: 10.5,
      z: 4,
    },
    rotation: {
      x: -1.3,
      y: -0.85,
      z: 0,
    },
  },
  {
    zoomOutPosition: {
      x: 0,
      y: 0,
      z: 30,
    },
    zoomOutRotation: {
      x: 0,
      y: 0,
      z: 0.7,
    },
    position: {
      x: 6,
      y: 5,
      z: 12,
    },
    rotation: {
      x: 0,
      y: 0.9,
      z: 0,
    },
  },
];

const responsivePositions = [
  { position: { x: 0, y: 0, z: 5 }, rotation: { y: 0, x: 0, z: 0 } },
  { position: { x: -5, y: 0, z: 5 }, rotation: { y: 0, x: 0, z: 0 } },
  { position: { x: 3, y: 1, z: 0 }, rotation: { y: 0, x: 0, z: 0 } },
  { position: { x: -5, y: 0, z: -1 }, rotation: { y: 0, z: 0, x: 0 } },
  { position: { x: 1, y: 0, z: -1 }, rotation: { x: 0, y: 0.5, z: 0 } },
];

const Camera = ({ currentSection, ...props }) => {
  const mousePosition = useMousePosition();
  const deviceOrientation = useDeviceOrientation();
  const cameraRef = useRef();
  const cameraGroup = useRef();
  const cameraResponsiveGroup = useRef();
  const currentSectionRef = useRef(useStore.getState().currentSection);
  const websiteReadyRef = useRef(false);

  const set = useThree((state) => {
    return state.set;
  });

  useEffect(() => {
    const unsubscribe1 = useStore.subscribe(
      (currentSection) => {
        currentSectionRef.current = currentSection;
        if (cameraGroup.current) {
          const cameraValues = camerasPositions[currentSection];

          cameraGroup.current.position.x = cameraValues.zoomOutPosition.x;
          cameraGroup.current.position.y = cameraValues.zoomOutPosition.y;
          cameraGroup.current.position.z = cameraValues.zoomOutPosition.z;

          cameraGroup.current.rotation.x = cameraValues.zoomOutRotation.x;
          cameraGroup.current.rotation.y = cameraValues.zoomOutRotation.y;
          cameraGroup.current.rotation.z = cameraValues.zoomOutRotation.z;

          gsap.to(cameraGroup.current.position, {
            x: cameraValues.position.x,
            y: cameraValues.position.y,
            z: cameraValues.position.z,
            duration: 1.4,
          });
          gsap.to(cameraGroup.current.rotation, {
            x: cameraValues.rotation.x,
            y: cameraValues.rotation.y,
            z: cameraValues.rotation.z,
            duration: 1.4,
          });
        }
        if (cameraResponsiveGroup.current) {
          if (window.matchMedia("(max-width:780px)").matches) {
            cameraResponsiveGroup.current.position.x =
              responsivePositions[currentSection].position.x;
            cameraResponsiveGroup.current.position.y =
              responsivePositions[currentSection].position.y;
            cameraResponsiveGroup.current.position.z =
              responsivePositions[currentSection].position.z;
            cameraResponsiveGroup.current.rotation.x =
              responsivePositions[currentSection].rotation.x;
            cameraResponsiveGroup.current.rotation.y =
              responsivePositions[currentSection].rotation.y;
            cameraResponsiveGroup.current.rotation.z =
              responsivePositions[currentSection].rotation.z;
          } else {
            cameraResponsiveGroup.current.position.x = 0;
            cameraResponsiveGroup.current.position.y = 0;
            cameraResponsiveGroup.current.position.z = 0;
            cameraResponsiveGroup.current.rotation.x = 0;
            cameraResponsiveGroup.current.rotation.y = 0;
            cameraResponsiveGroup.current.rotation.z = 0;
          }
        }
      },
      (state) => state.currentSection
    );
    const unsubscribe2 = useStore.subscribe(
      (cameraPosition) => {
        if (cameraPosition && cameraGroup.current) {
          gsap.to(cameraGroup.current.position, {
            x: cameraPosition.x,
            y: cameraPosition.y,
            z: cameraPosition.z,
            duration: 0.4,
          });
        }
      },
      (state) => state.cameraPosition
    );
    const unsubscribe3 = useStore.subscribe(
      (progress) => {
        websiteReadyRef.current = progress.ready;
        if (cameraGroup.current && progress.ready) {
          const cameraValues = camerasPositions[0];
          const cameraResponsiveValues = responsivePositions[0];
          gsap.to(cameraGroup.current.position, {
            x: cameraValues.position.x,
            y: cameraValues.position.y,
            z: cameraValues.position.z,
            duration: 1.4,
          });
          gsap.to(cameraGroup.current.rotation, {
            x: cameraValues.rotation.x,
            y: cameraValues.rotation.y,
            z: cameraValues.rotation.z,
            duration: 1.4,
          });
          if (
            cameraResponsiveGroup.current &&
            window.matchMedia("(max-width:780px)").matches
          ) {
            cameraResponsiveGroup.current.position.x =
              cameraResponsiveValues.position.x;
            cameraResponsiveGroup.current.position.y =
              cameraResponsiveValues.position.y;
            cameraResponsiveGroup.current.position.z =
              cameraResponsiveValues.position.z;
            cameraResponsiveGroup.current.rotation.x =
              cameraResponsiveValues.rotation.x;
            cameraResponsiveGroup.current.rotation.y =
              cameraResponsiveValues.rotation.y;
            cameraResponsiveGroup.current.rotation.z =
              cameraResponsiveValues.rotation.z;
          }
        }
      },
      (state) => state.progress
    );
    return () => {
      unsubscribe1();
      unsubscribe2();
      unsubscribe3();
    };
  }, []);

  useEffect(() => set({ camera: cameraRef.current }), [set]);

  useFrame(() => {
    const target = { x: mousePosition.x, y: mousePosition.y };
    const beta = deviceOrientation.beta * 0.002 || 0;
    const gamma = deviceOrientation.gamma * 0.002 || 0;

    target.x = mousePosition.x * 0.0001;
    target.y = mousePosition.y * 0.0001;

    if (websiteReadyRef) {
      if (window.matchMedia("(min-width:780px)").matches) {
        cameraRef.current.rotation.x +=
          0.05 * (target.y - cameraRef.current.rotation.x);
        cameraRef.current.rotation.y +=
          0.05 * (target.x - cameraRef.current.rotation.y);
      } else {
        cameraRef.current.rotation.x +=
          0.05 * (beta - cameraRef.current.rotation.x);
        cameraRef.current.rotation.y +=
          0.05 * (gamma - cameraRef.current.rotation.y);
      }
    }

    cameraRef.current.updateMatrixWorld();
  });

  return (
    <group ref={cameraResponsiveGroup}>
      <group
        ref={cameraGroup}
        position={Object.values(camerasPositions[0].zoomOutPosition)}
        rotation={Object.values(camerasPositions[0].zoomOutRotation)}
        {...props}
      >
        <PerspectiveCamera makeDefault far={3000} ref={cameraRef} />
      </group>
    </group>
  );
};

export default Camera;
